import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "h-full flex flex-col border border-gray bg-black" }
const _hoisted_2 = { class: "z-10 absolute p-2 top-2 left-2 rounded-full bg-white bg-opacity-80" }
const _hoisted_3 = { class: "p-2 flex justify-end absolute right-0 top-0 z-10" }
const _hoisted_4 = {
  key: 0,
  class: "h-full w-full flex items-center justify-center px-8"
}
const _hoisted_5 = {
  key: 1,
  class: "h-full w-full flex items-center justify-center px-8"
}

import { computed, inject, PropType, Ref, ref, watch } from 'vue';
import { useQueryClient } from 'vue-query';
import { ElMessageBox, ElNotification } from 'element-plus';

import { getDomainPath } from '@/services';
import { useGetInspectionsByInspectionGroupQuery } from '@/entities/inspection/hooks';
import {
  annotationsQueryName,
  useGetAnnotationQuery, useGetAnnotationsQuery, useUpdateAnnotationMutation,
} from '@/entities/annotation/hooks';
import {
  annotationGroupsQueryName,
  useCreateAnnotationGroupMutation,
} from '@/entities/annotation-group/hooks';
import { useGetInspectionGroupsByLastDeviceInspection } from '@/entities/inspection-group/hooks';

import { OSDExposition } from '@/core/components/data-display/OSD/osd.shared';
import { Inspection } from '@/entities/inspection/types';
import { Annotation } from '@/entities/annotation/types';
import { Device } from '@/entities/device/types';
import { InspectionGroup } from '@/entities/inspection-group/types';
import { FormatDateInjection } from '@/plugins/date-fns';

import { NkOSD, NkSelect, NkOption, NkHeading, NkButton } from '@/core/components';

import InspectionViewerAnnotation
  from '@/teams/visionair/components/InspectionViewer/InspectionViewerAnnotation.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'InspectionHistoryViewer',
  props: {
  annotation: {
    type: Number as PropType<Annotation['id']>,
    default: null,
  },
  inspection: {
    type: Number as PropType<Inspection['id']>,
    default: null,
  },
  device: {
    type: Number as PropType<Device['id']>,
    required: true,
  },
  inspectionGroup: {
    type: Number as PropType<InspectionGroup['id']>,
    required: true,
  },
},
  emits: ['close'],
  setup(__props) {

const props = __props





const osd = ref<OSDExposition | undefined>();
const queryClient = useQueryClient();
const $format: FormatDateInjection = inject('$format') as FormatDateInjection;

const selectedInspectionGroup = ref();
const activeAnnotation = ref<Annotation['id'] | null>(null);

const {
  mutateAsync: createAnnotationGroupMutation,
  isLoading: createAnnotationGroupIsLoading,
} = useCreateAnnotationGroupMutation();

const {
  mutateAsync: updateAnnotationMutation,
  isLoading: updateAnnotationIsLoading,
} = useUpdateAnnotationMutation();
const actionIsLoading = computed(() => updateAnnotationIsLoading.value || createAnnotationGroupIsLoading.value);

const inspectionsQueryData = computed(() => ({
  inspectionGroupId: selectedInspectionGroup.value,
  deviceId: props.device,
  wantParentDevice: false,
}));
const hasInspectionsQueryData = computed(() => !!(inspectionsQueryData.value.inspectionGroupId && inspectionsQueryData.value.deviceId));
const {
  data: inspection,
  refetch,
  isError,
} = useGetInspectionsByInspectionGroupQuery(inspectionsQueryData, {
  enabled: hasInspectionsQueryData,
});

const annotationsQueryFilters: Ref = computed(() => ({ ids: [...((inspection.value as Inspection)?.annotations || [])] }));
const hasAnnotationsQueryFilters = computed(() => !!annotationsQueryFilters.value.ids.length);
const { data: annotations } = useGetAnnotationsQuery({
  enabled: hasAnnotationsQueryFilters,
  initialData: [],
}, annotationsQueryFilters);

// Current annotation
const annotationQueryData = computed(() => ({ id: props.annotation }));
const hasAnnotationQueryData = computed(() => !!annotationQueryData.value.id);
const { data: annotation } = useGetAnnotationQuery(annotationQueryData, { enabled: hasAnnotationQueryData });

// History
const inspectionGroupsQueryData = computed(() => ({
  inspection: props.inspection,
}));
const hasInspectionGroupQueryData = computed(() => !!(inspectionGroupsQueryData.value.inspection));
const {
  data: inspectionGroupsData,
  isLoading: inspectionGroupIsLoading,
} = useGetInspectionGroupsByLastDeviceInspection(inspectionGroupsQueryData, {
  enabled: hasInspectionGroupQueryData,
  onSuccess: (data) => {
    const _data = data.filter(o => o.id !== props.inspectionGroup);

    if (_data.length && !selectedInspectionGroup.value) {
      selectedInspectionGroup.value = _data[0].id;
      return selectedInspectionGroup.value;
    }

    return null;
  },
});
const inspectionGroups = computed(() => inspectionGroupsData.value?.filter(o => o.id !== props.inspectionGroup)?.map(o => ({
  ...o,
  date: $format(o.date),
})) || []);

// states
const isAnnotationHighlight = ({ annotationGroupTypes }: Annotation) => {
  const history = annotation.value?.annotationGroupTypes?.history;
  return annotation.value && history && history === annotationGroupTypes?.history;
};

const refreshHistoryLinkState = async (linkedAnnotationId: Annotation['id']) => {
  await queryClient.refetchQueries(annotationGroupsQueryName, { active: true });
  await queryClient.refetchQueries([annotationsQueryName, { id: annotation.value?.id }]);
  await queryClient.refetchQueries([annotationsQueryName, { id: linkedAnnotationId }]);
  await queryClient.refetchQueries(annotationsQueryName, { active: true });
};

const createHistoryLink = async (toLinkAnnotation: Annotation) => {
  if (actionIsLoading.value || isAnnotationHighlight(toLinkAnnotation)) {
    return false;
  }

  const toLinkAnnotationGroupId = toLinkAnnotation.annotationGroupTypes?.history;
  const groupID = annotation.value?.annotationGroupTypes?.history;

  if (!annotation.value) {
    return false;
  }

  if (toLinkAnnotationGroupId) {
    await updateAnnotationMutation({
      ...annotation.value,
      annotationGroups: [
        ...annotation.value.annotationGroups.filter((annotationGroupId) => annotationGroupId !== groupID),
        toLinkAnnotationGroupId,
      ],
    });

    await refreshHistoryLinkState(toLinkAnnotation.id);
    return true;
  }

  if (groupID) {
    await updateAnnotationMutation({
      ...toLinkAnnotation,
      annotationGroups: [
        ...toLinkAnnotation.annotationGroups.filter((annotationGroupId) => annotationGroupId !== toLinkAnnotationGroupId),
        groupID,
      ],
    });

    await refreshHistoryLinkState(toLinkAnnotation.id);
    return true;
  }

  await createAnnotationGroupMutation({
    date: new Date(),
    comment: '',
    type: 'history',
    ids: [toLinkAnnotation.id, annotation.value.id],
  });

  await refreshHistoryLinkState(toLinkAnnotation.id);
  return true;
};

const onAnnotationClick = async (toLinkAnnotation: Annotation) => {
  if (!annotation.value || actionIsLoading.value || isAnnotationHighlight(toLinkAnnotation)) {
    return false;
  }

  try {
    await ElMessageBox.confirm(
      `Create a history link between annotation ${ annotation.value.id } and annotation ${ toLinkAnnotation.id }?`,
      'Confirm history link',
      {
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        type: 'warning',
      },
    );

    const hasLinked = await createHistoryLink(toLinkAnnotation);

    if (hasLinked) {
      ElNotification({
        type: 'success',
        position: 'bottom-right',
        message: 'History link created.',
      });
    }

    return hasLinked;
  } catch {
    return false;
  }
};

const onAnnotationFocus = (annotationId: Annotation['id']) => {
  activeAnnotation.value = annotationId;
};

const onAnnotationBlur = () => {
  activeAnnotation.value = null;
};

const dziPath = computed(() => getDomainPath(inspection.value?.dziPath));

watch(dziPath, (value) => {
  if (value) {
    osd.value?.updateViewer(dziPath.value);
  }
});

return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(NkButton), {
        title: "",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close'))),
        type: "white",
        icon: "NkClose",
        circle: ""
      })
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(NkSelect), {
        onChange: _cache[1] || (_cache[1] = () => _unref(refetch)()),
        modelValue: selectedInspectionGroup.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedInspectionGroup).value = $event))
      }, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(inspectionGroups), (group) => {
            return (_openBlock(), _createBlock(_unref(NkOption), {
              key: group.id,
              value: group.id,
              label: group.date
            }, null, 8, ["value", "label"]))
          }), 128))
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    (_unref(isError))
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(_unref(NkHeading), { type: "italic" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('notification.error')), 1)
            ]),
            _: 1
          })
        ]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (_unref(inspection)?.dziProcessDone && _unref(inspection).dziPath)
            ? (_openBlock(), _createBlock(_unref(NkOSD), {
                key: 0,
                ref_key: "osd",
                ref: osd,
                class: "w-full flex-1 bg-black",
                options: {
          tileSources: _unref(dziPath),
        }
              }, {
                default: _withCtx(() => [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(annotations), (a, i) => {
                    return (_openBlock(), _createBlock(InspectionViewerAnnotation, {
                      key: a.id,
                      index: i,
                      annotation: a.id,
                      highlight: isAnnotationHighlight(a),
                      title: `Annotation ${a.id}`,
                      "context-menu-options": {
              placement: 'left-start',
            },
                      class: _normalizeClass({
              'cursor-copy': props.annotation && !isAnnotationHighlight(a)
            }),
                      onClick: ($event: any) => (onAnnotationClick(a)),
                      onFocusin: _cache[3] || (_cache[3] = () => {}),
                      onFocusout: _cache[4] || (_cache[4] = () => {}),
                      onMouseenter: () => onAnnotationFocus(a.id),
                      onMouseleave: _cache[5] || (_cache[5] = () => onAnnotationBlur())
                    }, null, 8, ["index", "annotation", "highlight", "title", "class", "onClick", "onMouseenter"]))
                  }), 128))
                ]),
                _: 1
              }, 8, ["options"]))
            : (!_unref(inspection)?.dziProcessDone)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  (!_unref(inspection)?.dziPath)
                    ? (_openBlock(), _createBlock(_unref(NkHeading), {
                        key: 0,
                        type: "italic"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_ctx.$t('inspection.viewer.messages.dzi_missing')), 1)
                        ]),
                        _: 1
                      }))
                    : (_unref(inspection)?.dziProgress === 'Missing image')
                      ? (_openBlock(), _createBlock(_unref(NkHeading), {
                          key: 1,
                          type: "italic"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$t('inspection.viewer.messages.missing_image')), 1)
                          ]),
                          _: 1
                        }))
                      : (_openBlock(), _createBlock(_unref(NkHeading), {
                          key: 2,
                          type: "italic"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$t('inspection.viewer.messages.slicing_in_progress')), 1)
                          ]),
                          _: 1
                        }))
                ]))
              : _createCommentVNode("", true)
        ], 64))
  ])), [
    [_directive_loading, _unref(inspectionGroupIsLoading) || _unref(actionIsLoading)]
  ])
}
}

})